import { useParams, Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import MouseFollowBackground from "@/components/MouseFollowBackground";
import PageTransition from "@/components/PageTransition";
import imsirunHero from "@/assets/imsirun-01.jpg";
import alexandriteImage from "@/assets/alexandrite-campaign.jpeg";
import antherisImage from "@/assets/antheris-branding.jpeg";
import barberbroImage from "@/assets/barberbro-branding.jpeg";
import imsirunGallery4 from "@/assets/imsirun-gallery-4.jpg";
import imsirunGallery5 from "@/assets/imsirun-gallery-5.jpg";
import imsirunGallery6 from "@/assets/imsirun-gallery-6.jpg";
import imsirunGallery7 from "@/assets/imsirun-gallery-7.jpg";
import imsirunGallery8 from "@/assets/imsirun-gallery-8.jpg";
import imsirunGallery9 from "@/assets/imsirun-gallery-9.jpg";
import imsirunGallery10 from "@/assets/imsirun-gallery-10.jpg";
import imsirunGallery11 from "@/assets/imsirun-gallery-11.jpg";
import imsirunGallery12 from "@/assets/imsirun-gallery-12.jpg";
import alexandriteGallery1 from "@/assets/alexandrite-gallery-1.jpg";
import alexandriteGallery2 from "@/assets/alexandrite-gallery-2.jpg";
import alexandriteGallery3 from "@/assets/alexandrite-gallery-3.jpg";
import alexandriteGallery4 from "@/assets/alexandrite-gallery-4.jpg";
import alexandriteGallery5 from "@/assets/alexandrite-gallery-5.jpg";
import alexandriteGallery6 from "@/assets/alexandrite-gallery-6.jpg";
import alexandriteGallery7 from "@/assets/alexandrite-gallery-7.jpg";
import alexandriteGallery8 from "@/assets/alexandrite-gallery-8.jpg";
import alexandriteGallery9 from "@/assets/alexandrite-gallery-9.jpg";
import barberbroGallery1 from "@/assets/barberbro-gallery-1.jpg";
import barberbroGallery2 from "@/assets/barberbro-gallery-2.jpg";
import barberbroGallery3 from "@/assets/barberbro-gallery-3.jpg";
import barberbroGallery4 from "@/assets/barberbro-gallery-4.jpg";
import barberbroGallery5 from "@/assets/barberbro-gallery-5.jpg";
import barberbroGallery6 from "@/assets/barberbro-gallery-6.jpg";
import barberbroGallery7 from "@/assets/barberbro-gallery-7.jpg";
import barberbroGallery8 from "@/assets/barberbro-gallery-8.jpg";
import barberbroGallery9 from "@/assets/barberbro-gallery-9.jpg";
import barberbroGallery10 from "@/assets/barberbro-gallery-10.jpg";
import barberbroGallery11 from "@/assets/barberbro-gallery-11.jpg";
import barberbroGallery12 from "@/assets/barberbro-gallery-12.jpg";
import barberbroGallery13 from "@/assets/barberbro-gallery-13.jpg";

interface CaseStudySection {
  title: string;
  content: string[];
}

interface CaseStudyData {
  introduction: string[];
  companyBackground: string[];
  problemStatement: CaseStudySection[];
  strategicResponse: CaseStudySection[];
  implementation: CaseStudySection[];
  outcomes: {
    keyResults: string[];
    performanceData: {
      month: number;
      newClients: number;
      returningClients: number;
      giftCardsSold: number;
      retentionRate: string;
    }[];
  };
  conclusion: CaseStudySection[];
}

interface BudgetData {
  channels: { name: string; amount: string }[];
  totalMonthly: string;
  revenue?: {
    monthlyPatients: string;
    avgRevenuePerPatient: string;
    totalMonthlyRevenue: string;
  };
  romi?: string;
  netReturn?: string;
  note?: string;
}

interface ProjectData {
  title: string;
  tags: string[];
  heroImage: string;
  overview: string;
  timeline?: string;
  challenge: string;
  role?: string[];
  strategy?: string[];
  tactics?: string[];
  solution?: string;
  results: string[];
  budget?: BudgetData;
  assetsToShow?: string;
  keyLearning?: string;
  gallery: string[];
  videoUrl?: string;
  caseStudy?: CaseStudyData;
}

const portfolioProjects: Record<string, ProjectData> = {
  "imsirun-skincare": {
    title: "Imsirun Skincare Packages Launch",
    tags: ["Branding", "Influencers", "Media Buying", "Social Content", "Outdoor Ads"],
    heroImage: imsirunHero,
    overview: "A full-funnel launch for Imsirun — a premium skincare procedure package line. The project included brand development, OOH and indoor advertising, an influencer ecosystem, a social-first performance funnel, and full photo/video asset production.",
    timeline: "8 months (brand creation → market launch)",
    challenge: "Introduce a new line of skincare procedures in a saturated aesthetics market and build fast, credible awareness that drives consultations and bookings.",
    role: [
      "Brand positioning and architecture",
      "Launch roadmap and integrated media plan",
      "Influencer partnerships and PR-style collaborations",
      "Creative direction for photoshoots and video content",
      "Cross-channel performance marketing strategy"
    ],
    strategy: [
      "Develop a brand narrative centered on one core benefit: visible skin improvement through medically guided procedures.",
      "Use a hybrid media mix to generate reach (OOH + indoor placements) and drive conversions (targeted social ads + influencer UGC).",
      "Create content for each funnel stage: Awareness (hero video, OOH visuals), Consideration (dermatologist-led explanations, influencer reviews), Conversion (tailored landing pages, retargeting ads, service benefits breakdown)."
    ],
    tactics: [
      "Designed a visual identity to signal trust, expertise, and clinical-grade quality.",
      "Directed large-scale photoshoots (hero, lifestyle, treatment-room content).",
      "Ran OOH campaigns in high-traffic urban areas and indoor screens in partner clinics.",
      "Built an influencer ecosystem including skincare creators and YouTube podcasters relevant to aesthetic medicine.",
      "Implemented performance ads on Meta and Google with interest-based, lookalike, and intent-driven targeting.",
      "Launched conversion-focused landing pages with A/B-tested CTAs, package descriptions, and lead-generation forms."
    ],
    results: [
      "Successfully built strong brand awareness across key audiences within the first year.",
      "Imsirun became one of the clinic's most recognizable procedure lines shortly after launch.",
      "Booking volume grew steadily following the campaign rollout, supported by positive customer reviews.",
      "Influencer collaborations generated high engagement and authentic interest in the procedure results."
    ],
    budget: {
      channels: [
        { name: "Influencers", amount: "21%" },
        { name: "Outdoor Advertising (OOH)", amount: "62%" },
        { name: "Social Media Ads", amount: "17%" }
      ],
      totalMonthly: "100%",
      revenue: {
        monthlyPatients: "+1,900%",
        avgRevenuePerPatient: "33%",
        totalMonthlyRevenue: "+200%"
      },
      romi: "2.0x",
      netReturn: "+200%",
      note: "Marketing accounted for ~33% of revenue, which is healthy for a brand-building + performance hybrid model"
    },
    gallery: [imsirunGallery6, imsirunGallery5, imsirunGallery7, imsirunGallery4, imsirunGallery8, imsirunGallery9, imsirunGallery10, imsirunGallery11, imsirunGallery12],
    videoUrl: "https://www.youtube.com/watch?v=sECtsa3aXNI",
    caseStudy: {
      introduction: [
        "The Armenian skincare and aesthetic wellness market has undergone fast-paced growth in the last decade, driven by rising interest in self-care, the influence of social media, and increased accessibility of dermatological treatments. Younger consumers, especially urban women, are increasingly exposed to global beauty standards through platforms like Instagram, TikTok, and YouTube, where skincare routines, \"before and after\" stories, and product reviews shape aspirations and expectations.",
        "Yet despite this momentum, the sector remains fragmented and underdeveloped compared with Western Europe, the United States, and East Asia. Armenia's more than 500 beauty salons, dermatology centers, and medical spas operate with limited regulatory oversight, minimal standardization, and wide variation in service quality.",
        "Industry-wide, preventive skincare remains a relatively new concept. A significant portion of consumers still approaches cosmetic care reactively—seeking treatment for visible issues such as acne, pigmentation, or wrinkles only after they become problematic.",
        "Against this backdrop, Imsirun entered as a brand whose ambition went beyond filling capacity in clinic rooms. It aimed to professionalize and structure Armenia's skincare landscape by offering predictable, measurable, and emotionally engaging experiences around skin health and wellness."
      ],
      companyBackground: [
        "Imsirun was launched in 2023 by Antheris, a leading aesthetic medical network with 10 branches and over 150 employees across six cities in Armenia. Antheris had already built strong recognition in medical aesthetics and laser treatments, giving Imsirun a foundation of trust and clinical expertise from day one.",
        "The brand name, \"Imsirun\" (\"my beautiful\"), encapsulates its philosophy: skincare should feel both personally meaningful and emotionally affirming. Rather than framing services solely in medical or cosmetic terms, the brand positions itself at the intersection of clinical precision and emotional care.",
        "Imsirun introduced 16 specialized skincare packages, each designed to address a specific combination of needs such as hydration, anti-aging, acne scars, or skin tone correction. These packages were structured as multi-step journeys rather than isolated treatments.",
        "The brand's business model also reflected innovation. Through subscription-based skincare plans, Imsirun invited clients to view skin health as a long-term project. Monthly or six-month formats encouraged regular visits, follow-ups, and consistent care."
      ],
      problemStatement: [
        {
          title: "Scalability of Personalization",
          content: ["As demand increased, the brand's model of highly personalized care—carefully curated programs, in-depth consultations, and device-specific protocols—became harder to replicate across multiple branches. The risk was that service quality and experience could vary from clinic to clinic, weakening brand consistency."]
        },
        {
          title: "Shifting and Rising Consumer Expectations",
          content: ["Consumers, particularly younger ones, were no longer satisfied with simple \"treatment menus.\" They wanted integrated experiences: expert advice, long-term planning, lifestyle guidance, and emotional reassurance. For Imsirun, this meant upgrading from transactional service delivery to a holistic relationship model."]
        },
        {
          title: "Intensifying Competition from Indirect Players",
          content: ["While Imsirun faced no direct competitor with equivalent technology and structured programs at launch, it operated in a market with over 500 indirect competitors—beauty salons, medical centers, and independent cosmetologists. Many offered cheaper services and contributed to a perception that \"basic skincare\" can be obtained anywhere."]
        },
        {
          title: "Limited Awareness of Preventive Skincare",
          content: ["Armenian consumers were used to viewing skincare as corrective, not preventive. The concept of signing up for a six-month plan to improve and maintain skin health required a change in mindset."]
        },
        {
          title: "Price Sensitivity and Economic Constraints",
          content: ["While Imsirun targeted the mid-premium segment, economic volatility meant that customers were still sensitive to price and needed clear, value-based rationales for committing to packages and subscriptions."]
        }
      ],
      strategicResponse: [
        {
          title: "Strategic Positioning: Owning the Mid-Premium Wellness Space",
          content: ["Rather than competing head-on with luxury medspas or low-cost salons, Imsirun chose to own the mid-premium territory—high value, structured services at accessible but not bargain-level prices. This included a focus on visible, measurable results."]
        },
        {
          title: "Service Innovation: From Procedures to Programs",
          content: [
            "Imsirun innovated in how services were framed and delivered. Instead of isolated offerings, it developed:",
            "• Age-based and need-based skincare packages",
            "• Six-month structured treatment plans incorporating multiple devices and stages",
            "• Integrated wellness elements such as relaxation, ambiance, and self-care rituals",
            "This shifted the conversation from \"What single treatment do I want?\" to \"What journey will help me get where I want to be?\""
          ]
        },
        {
          title: "Technology as a Competitive Moat",
          content: ["Vendor exclusivity for Thulium and Frax laser technologies provided a temporary but significant competitive advantage. These technologies became central to Imsirun's higher-tier programs and were highlighted in educational campaigns as key enablers of results that could not be easily copied."]
        },
        {
          title: "Educational, Influencer-Led Communication",
          content: [
            "Recognizing that behavioral change required education, Imsirun built a content and influencer strategy that explained:",
            "• Why structured skincare matters",
            "• How subscriptions make care more consistent",
            "• What advanced technologies actually do",
            "Over 100 local influencers—including lifestyle bloggers, beauty content creators, and public figures—helped normalize regular skincare and demystify treatments."
          ]
        },
        {
          title: "Value-Based Pricing and Incentives",
          content: [
            "Imsirun employed a value-based pricing approach:",
            "• Discounts on popular services to reduce entry barriers",
            "• Bonus Alexandrite sessions within subscriptions to increase perceived value",
            "• Packages positioned as wellness investments rather than one-off splurges"
          ]
        },
        {
          title: "CRM and Relationship Orientation",
          content: [
            "The brand treated each client not just as a transaction, but as a long-term partner. Systems were built around:",
            "• Regular follow-up calls",
            "• Progress tracking",
            "• Tailored recommendations",
            "• Special offers aligned with treatment milestones",
            "Imsirun's strategy can be summarized as an attempt to build a \"skincare habit ecosystem\" rather than a catalogue of treatments."
          ]
        }
      ],
      implementation: [
        {
          title: "Product Development, Segmentation & Personas",
          content: [
            "Imsirun's teams co-created treatments for three key personas:",
            "• Young Adult (~25): Digital, experimental, seeking acne control and glow. Drawn to structured plans that simplify choices.",
            "• Mid-Life Professional (~40): Busy, outcome-focused, willing to invest in anti-aging if results are credible and convenient.",
            "• Senior (~60): Values relaxation, indulgence, and safety. Appreciates spa-like environments and personal interaction.",
            "This segmentation influenced package design, marketing messages, scheduling options, and follow-up styles."
          ]
        },
        {
          title: "Value-Based Pricing in Practice",
          content: [
            "Instead of pushing the highest-priced packages, Imsirun:",
            "• Offered \"gateway\" programs at accessible price points",
            "• Demonstrated the cost-benefit of subscriptions compared to ad hoc treatments",
            "• Emphasized cost predictability and long-term benefits"
          ]
        },
        {
          title: "Integrated Marketing Communications (IMC)",
          content: [
            "Imsirun executed a multi-channel IMC strategy:",
            "• Outdoor media in high-traffic urban zones to build mass awareness",
            "• TV sponsorship, associating the brand with Armenia's most-watched show and signaling scale and seriousness",
            "• A podcast partnership targeting women 18–35, featuring discussions on topics such as relationships, work-life balance, and psychological themes",
            "• Social media campaigns with tutorials, Q&As, and behind-the-scenes content from clinics",
            "The IMC strategy reinforced the same narrative: skincare as structured self-care, not occasional luxury."
          ]
        },
        {
          title: "Client Lifecycle Management and Feedback Loops",
          content: [
            "The centralized call center became an engine for relationship management:",
            "• Immediately after treatments, clients received check-in calls",
            "• At around 10 days, follow-ups explored early results and addressed concerns",
            "• Monthly communication sustained engagement with tips, reminders, and offers",
            "Insights from these interactions informed ongoing service improvements, allowing Imsirun to refine protocols and adjust programs based on real-world feedback."
          ]
        },
        {
          title: "Pilot Programs",
          content: [
            "Pilot testing over three months confirmed that:",
            "• Bundled services outperformed single sessions in satisfaction and retention",
            "• Relaxation-oriented elements enhanced perceived value",
            "• Structured plans were particularly appreciated by professionals with limited time",
            "The pilots validated Imsirun's core thesis that structure, clarity, and emotional experience would be key to winning the Armenian consumer."
          ]
        }
      ],
      outcomes: {
        keyResults: [
          "Monthly visits increased sixfold over 12 months",
          "Gift card sales reached 1,000 during Women's Day campaigns",
          "The brand gained recognition as a leader in wellness-integrated skincare",
          "Consumer perception shifted—from \"occasional beauty\" to \"ongoing wellness and self-care\"",
          "Subscriptions and gift cards provided recurring and seasonal revenue streams"
        ],
        performanceData: [
          { month: 1, newClients: 100, returningClients: 0, giftCardsSold: 50, retentionRate: "0%" },
          { month: 3, newClients: 200, returningClients: 55, giftCardsSold: 150, retentionRate: "27.5%" },
          { month: 6, newClients: 400, returningClients: 150, giftCardsSold: 300, retentionRate: "37.5%" },
          { month: 12, newClients: 600, returningClients: 350, giftCardsSold: 500, retentionRate: "58%" }
        ]
      },
      conclusion: [
        {
          title: "Redefining the Category",
          content: ["Imsirun moved the local conversation from \"Which treatment should I do once?\" to \"How do I care for my skin over months and years?\". That conceptual shift is central to long-term customer value."]
        },
        {
          title: "Blending Clinical and Emotional Value",
          content: ["By offering advanced dermatological devices within a warm, spa-like environment, Imsirun captured both rational and emotional drivers of consumer decision-making."]
        },
        {
          title: "Education as a Growth Engine",
          content: ["Influencer partnerships, podcasts, and educational content helped bridge the knowledge gap around preventive skincare, making complex technologies and multi-stage programs understandable and aspirational."]
        },
        {
          title: "Subscription Models in a Price-Sensitive Market",
          content: ["Adapting subscription logic to local economic realities created a sense of affordability and psychological safety: clients knew what to expect financially and emotionally over six months."]
        },
        {
          title: "Co-Creation with Customers",
          content: ["Feedback mechanisms allowed clients to shape the evolution of packages and services, reinforcing trust and loyalty."]
        },
        {
          title: "Localized Strategy, Global Logic",
          content: ["While Imsirun's approach resonates with global trends in wellness and dermatology, its success relied on deep alignment with Armenia's cultural norms, economic conditions, and consumer attitudes. Rather than importing a foreign template, the brand anchored its vision in local realities."]
        }
      ]
    }
  },
  "alexandrite-campaign": {
    title: "Alexandrite Price-Drop Campaign",
    tags: ["Ads", "Content", "Influencers", "Video Production", "OOH"],
    heroImage: alexandriteImage,
    overview: "An integrated campaign promoting Alexandrite laser hair removal following a strategic price reduction. Executed across OOH, influencers, paid social, and clinic-specific indoor channels to rapidly scale bookings and acquire new customers in a highly competitive beauty market.",
    timeline: "6 weeks (concept → launch → optimization)",
    challenge: "Communicate a price drop for a high-demand service in an oversaturated beauty market—while preserving perceived quality and converting awareness into immediate booking growth and first-time patient acquisition.",
    role: [
      "End-to-end campaign leadership and creative direction",
      "Message architecture, hero concept, and offer positioning",
      "Digital channel strategy and budget ownership",
      "Oversight of video and photo asset production",
      "Influencer activation and coordination",
      "Continuous performance optimization across channels"
    ],
    strategy: [
      "Built a bold, highly simplified value message centered on newly accessible pricing.",
      "Combined OOH + paid social for mass awareness, reinforced by influencer and UGC content for credibility.",
      "Applied urgency-driven messaging to accelerate decision-making and shorten the booking cycle."
    ],
    tactics: [
      "Produced short-form video ads for Meta, TikTok, and Reels.",
      "Launched OOH billboards and indoor clinic screens to increase frequency and recall.",
      "Activated niche beauty influencers to demonstrate treatment quality while clearly communicating the price drop.",
      "Ran Meta campaigns targeting high-intent audiences and warm retargeting pools.",
      "Conducted A/B testing across creatives, CTAs, and offer framing to improve CTR and reduce CPA."
    ],
    results: [
      "Patient base growth: Total patients increased from ~15,000 to 25,000+ within 3 months following the campaign. Represents a +40% growth in overall patient volume.",
      "Market share impact: Estimated total market size: ~150,000 potential patients. Market share increased from ~10% to ~16.6%, driven primarily by Alexandrite demand.",
      "Customer acquisition: Strong surge in first-time patients, many unfamiliar with the clinic prior to the campaign. Price-drop messaging proved especially effective at converting high-intent but previously hesitant audiences.",
      "Efficiency gains: Noticeable reduction in cost per lead, driven by simplified messaging and creative optimization. Higher CTRs observed on value-focused ads versus feature-heavy explanations."
    ],
    assetsToShow: "OOH creatives • Indoor screens • Influencer videos • Paid social ads • Before/after visuals",
    keyLearning: "Clear, value-focused messaging significantly outperformed detailed explanations — simplicity increased both trust and conversions.",
    gallery: [alexandriteGallery3, alexandriteGallery8, alexandriteGallery5, alexandriteGallery7, alexandriteGallery1, alexandriteGallery2, alexandriteGallery4, alexandriteGallery6, alexandriteGallery9],
    videoUrl: "https://www.youtube.com/watch?v=_Yvs2goXheA",
    budget: {
      channels: [
        { name: "Influencers", amount: "18%" },
        { name: "Outdoor advertising + Video production", amount: "70%" },
        { name: "Social media ads", amount: "12%" }
      ],
      totalMonthly: "100%",
      revenue: {
        monthlyPatients: "+40%",
        avgRevenuePerPatient: "9.5%",
        totalMonthlyRevenue: "+953%"
      },
      romi: "10.5x",
      netReturn: "+953%",
      note: "Marketing accounted for ~9.5% of revenue — highly efficient for a performance-driven awareness campaign (across 10 branches)"
    }
  },
  "antheris-rebranding": {
    title: "Antheris Clinics",
    tags: ["Identity", "Messaging", "Website", "Content", "Visuals"],
    heroImage: antherisImage,
    overview: "Complete rebranding of Avanta Clinics into Antheris — identity, brand voice, website updates, launch communications, and multi-channel rollout.",
    timeline: "6–8 months (concept → transformation → public launch)",
    challenge: "Refresh an established clinic's identity to better match its evolving services and audience expectations — without losing existing clients or brand recognition.",
    role: [
      "Brand transition lead",
      "Directed full brand identity development",
      "Defined communication tone, messaging pillars & brand story",
      "Led creative teams (design, content, video)",
      "Oversaw website redesign and new content structure",
      "Managed launch communications across digital + offline channels"
    ],
    strategy: [
      "Build a modern, clean, premium identity aligned with trust, expertise, and innovation.",
      "Ensure the transition is clear and positive, not confusing or risky for existing clients.",
      "Use strong visual storytelling to signal evolution, not replacement."
    ],
    tactics: [
      "New logo, color system, typography, and brand visuals",
      "Unified content style for social, website, OOH, and internal materials",
      "Website updates with new design sections, service pages, and brand elements",
      "Video content communicating the new identity",
      "Digital reveal campaign across social & email",
      "Updated in-clinic signage, print materials, and staff uniforms"
    ],
    results: [
      "Successful brand transition with positive client perception",
      "Increased engagement on social channels after the new identity launch",
      "Stronger coherence across all digital + offline touchpoints"
    ],
    assetsToShow: "Before/after brand identity, new website visuals, reveal video, social rollout.",
    keyLearning: "Clear communication and synchronized rollout across all channels ensured a seamless, confidence-boosting rebrand.",
    gallery: [],
    videoUrl: "https://www.youtube.com/watch?v=aA3lotCZIRQ"
  },
  "barberbro-branding": {
    title: "BarberBro Package for Men",
    tags: ["Brand Identity", "Messaging", "Visual Design"],
    heroImage: barberbroImage,
    overview: "Complete brand identity and communication concept for Alexandrite Laser Hair Removal for Men — an affordable, practical service package designed for modern men who want long-lasting grooming results.",
    challenge: "Create a clear, approachable identity that positions Alexandrite laser hair removal as a practical grooming solution for men — especially for body areas commonly maintained through shaving — while communicating the long-lasting benefits (up to 40-day intervals between sessions).",
    role: [
      "Service identity development",
      "Messaging and positioning direction",
      "Visual communication system creation",
      "Color palette and typography selection"
    ],
    results: [
      "Developed a straightforward, masculine identity that normalizes laser hair removal as part of regular grooming for men.",
      "Created messaging focused on practicality: less shaving, fewer ingrown hairs, cleaner look, and long-lasting intervals between treatments.",
      "Built a cohesive visual system suitable for use across ads, social media assets, and indoor clinic materials, supporting future male-focused campaigns."
    ],
    gallery: [barberbroGallery3, barberbroGallery9, barberbroGallery2, barberbroGallery5, barberbroGallery8, barberbroGallery6, barberbroGallery1, barberbroGallery7, barberbroGallery4, barberbroGallery10, barberbroGallery11, barberbroGallery12, barberbroGallery13],
    videoUrl: "https://www.youtube.com/watch?v=djN9SXKXJeM"
  }
};

const PortfolioDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const project = portfolioProjects[slug as keyof typeof portfolioProjects];

  // Scroll to top when component mounts or location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <MouseFollowBackground />
        <div className="text-center relative z-10">
          <h1 className="text-3xl font-display font-bold text-foreground mb-4">Project Not Found</h1>
          <Link to="/#portfolio">
            <Button variant="outline">Back to Portfolio</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen">
        <MouseFollowBackground />
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12 relative z-10">
        {/* Back Button */}
        <Link to="/#portfolio" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-lg">Back to Portfolio</span>
        </Link>

        {/* Hero Image */}
        <div className="rounded-2xl overflow-hidden mb-10">
          <img 
            src={project.heroImage} 
            alt={`${project.title} - Hero image for ${project.tags.slice(0, 3).join(', ')} marketing project by Any Hakobyan`}
            className="w-full h-auto object-contain"
          />
        </div>

        {/* Title & Tags */}
        <h1 className="text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
          {project.title}
        </h1>
        
        <div className="flex flex-wrap gap-3 mb-10">
          {project.tags.map((tag) => (
            <span 
              key={tag} 
              className="text-base px-4 py-2 rounded-full bg-primary/10 text-primary font-medium"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Overview */}
        <div className="mb-12">
          <h2 className="text-2xl font-display font-semibold text-foreground mb-4">Overview</h2>
          <p className="text-xl text-muted-foreground leading-relaxed">{project.overview}</p>
        </div>


        {/* Timeline */}
        {project.timeline && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">Timeline</h2>
            <p className="text-xl text-muted-foreground leading-relaxed">{project.timeline}</p>
          </div>
        )}

        {/* Challenge */}
        <div className="mb-12">
          <h2 className="text-2xl font-display font-semibold text-foreground mb-4">Challenge</h2>
          <p className="text-xl text-muted-foreground leading-relaxed">{project.challenge}</p>
        </div>

        {/* My Role */}
        {project.role && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">My Role</h2>
            <p className="text-lg text-muted-foreground mb-4">Lead strategist and project owner responsible for:</p>
            <ul className="space-y-3">
              {project.role.map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-xl text-muted-foreground">
                  <span className="w-2 h-2 rounded-full bg-primary mt-3 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Strategy */}
        {project.strategy && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">Strategy</h2>
            <ul className="space-y-3">
              {project.strategy.map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-xl text-muted-foreground">
                  <span className="w-2 h-2 rounded-full bg-primary mt-3 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Tactics */}
        {project.tactics && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">Tactics</h2>
            <ul className="space-y-3">
              {project.tactics.map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-xl text-muted-foreground">
                  <span className="w-2 h-2 rounded-full bg-primary mt-3 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Solution (for projects without detailed breakdown) */}
        {project.solution && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">The Solution</h2>
            <p className="text-xl text-muted-foreground leading-relaxed">{project.solution}</p>
          </div>
        )}

        {/* Results */}
        <div className="mb-12">
          <h2 className="text-2xl font-display font-semibold text-foreground mb-4">Results</h2>
          <ul className="space-y-3">
            {project.results.map((result, index) => (
              <li key={index} className="flex items-start gap-3 text-xl text-muted-foreground">
                <span className="w-2 h-2 rounded-full bg-primary mt-3 flex-shrink-0" />
                {result}
              </li>
            ))}
          </ul>
        </div>


        {/* Assets to Show */}
        {project.assetsToShow && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">Assets</h2>
            <p className="text-xl text-muted-foreground leading-relaxed">{project.assetsToShow}</p>
          </div>
        )}

        {/* Key Learning */}
        {project.keyLearning && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4">Key Learning</h2>
            <p className="text-xl text-muted-foreground leading-relaxed italic">{project.keyLearning}</p>
          </div>
        )}

        {/* Case Study */}
        {project.caseStudy && (
          <div className="mb-16 mt-16 pt-12 border-t border-border">
            <Collapsible>
              <CollapsibleTrigger className="w-full group">
                <div className="flex items-center justify-between mb-8">
                  <div className="text-left">
                    <span className="text-sm uppercase tracking-widest text-primary font-medium">In-Depth Analysis</span>
                    <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mt-2">Full Case Study</h2>
                    <p className="text-lg text-muted-foreground mt-3">Written by: Ani Hakobyan, Head of Marketing Department, Antheris Clinics; Tata Tcholaria, CEO of AWE Consulting; Luka Usenashvili, Deputy CEO & CFO of AWE Consulting; Nato Toronjadze, Vice Dean of Business School, Georgian American University</p>
                  </div>
                  <ChevronDown className="h-8 w-8 text-primary transition-transform duration-300 group-data-[state=open]:rotate-180" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                {/* Introduction & Market Background */}
            <div className="mb-12">
              <h3 className="text-2xl font-display font-semibold text-foreground mb-4">Introduction & Market Background</h3>
              <div className="space-y-4">
                {project.caseStudy.introduction.map((paragraph, index) => (
                  <p key={index} className="text-lg text-muted-foreground leading-relaxed">{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Company Background */}
            <div className="mb-12">
              <h3 className="text-2xl font-display font-semibold text-foreground mb-4">Company Background</h3>
              <div className="space-y-4">
                {project.caseStudy.companyBackground.map((paragraph, index) => (
                  <p key={index} className="text-lg text-muted-foreground leading-relaxed">{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Problem Statement */}
            <div className="mb-12">
              <h3 className="text-2xl font-display font-semibold text-foreground mb-6">Problem Statement</h3>
              <div className="space-y-6">
                {project.caseStudy.problemStatement.map((section, index) => (
                  <div key={index} className="pl-4 border-l-2 border-primary/30">
                    <h4 className="text-xl font-display font-medium text-foreground mb-2">{section.title}</h4>
                    {section.content.map((text, i) => (
                      <p key={i} className="text-lg text-muted-foreground leading-relaxed">{text}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Strategic Response */}
            <div className="mb-12">
              <h3 className="text-2xl font-display font-semibold text-foreground mb-6">Strategic Response</h3>
              <div className="space-y-8">
                {project.caseStudy.strategicResponse.map((section, index) => (
                  <div key={index} className="bg-card/30 rounded-xl p-6 border border-border/50">
                    <h4 className="text-xl font-display font-medium text-foreground mb-3">{section.title}</h4>
                    <div className="space-y-2">
                      {section.content.map((text, i) => (
                        <p key={i} className="text-lg text-muted-foreground leading-relaxed">{text}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Implementation */}
            <div className="mb-12">
              <h3 className="text-2xl font-display font-semibold text-foreground mb-6">Implementation</h3>
              <div className="space-y-8">
                {project.caseStudy.implementation.map((section, index) => (
                  <div key={index}>
                    <h4 className="text-xl font-display font-medium text-foreground mb-3">{section.title}</h4>
                    <div className="space-y-2">
                      {section.content.map((text, i) => (
                        <p key={i} className="text-lg text-muted-foreground leading-relaxed">{text}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Results (Quantitative Impact) */}
            <div className="mb-12">
              <h3 className="text-2xl font-display font-semibold text-foreground mb-6">Results (Quantitative Impact)</h3>
              
              {/* Patient Volume Growth */}
              <div className="mb-8">
                <h4 className="text-xl font-display font-medium text-foreground mb-4">Patient volume growth:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-lg text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                    Initial launch period: ~30 patients per month
                  </li>
                  <li className="flex items-start gap-3 text-lg text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                    End of year: ~600 patients per month
                  </li>
                  <li className="flex items-start gap-3 text-lg text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                    Represents a 20× increase in monthly patient volume over the campaign period
                  </li>
                </ul>
              </div>

              {/* Patient Dynamics */}
              <div className="mb-8">
                <h4 className="text-xl font-display font-medium text-foreground mb-4">Patient dynamics:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-lg text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                    Returning patients: ~450 per month by year-end
                  </li>
                  <li className="flex items-start gap-3 text-lg text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                    New patients: ~150 per month, driven primarily by paid social and influencer content
                  </li>
                  <li className="flex items-start gap-3 text-lg text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                    Strong retention indicated by a returning-patient share of ~75%
                  </li>
                </ul>
              </div>

              {/* Business Impact */}
              <div className="mb-8">
                <h4 className="text-xl font-display font-medium text-foreground mb-4">Business impact:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-lg text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                    Imsirun became one of the clinic's most recognizable and frequently booked procedure lines within the first year
                  </li>
                  <li className="flex items-start gap-3 text-lg text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                    Sustained month-over-month growth in bookings following the full-funnel rollout
                  </li>
                  <li className="flex items-start gap-3 text-lg text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                    Influencer collaborations delivered high engagement and trust-driven demand, directly contributing to first-time consultations and repeat bookings
                  </li>
                </ul>
              </div>
            </div>

            {/* Conclusion & Strategic Takeaways */}
            <div className="mb-12">
              <h3 className="text-2xl font-display font-semibold text-foreground mb-6">Conclusion & Strategic Takeaways</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {project.caseStudy.conclusion.map((section, index) => (
                  <div key={index} className="bg-primary/5 rounded-xl p-5 border border-primary/10">
                    <h4 className="text-lg font-display font-medium text-foreground mb-2">{section.title}</h4>
                    {section.content.map((text, i) => (
                      <p key={i} className="text-base text-muted-foreground leading-relaxed">{text}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Video */}
        {project.videoUrl && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-6">Video</h2>
            <div className="aspect-video rounded-xl overflow-hidden">
              <iframe
                src={project.videoUrl.replace("watch?v=", "embed/")}
                title={`${project.title} - Video`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Gallery */}
        {project.gallery.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-6">Gallery</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {project.gallery.map((image, index) => (
                <div key={index} className="aspect-[3/2] rounded-xl overflow-hidden">
                  <img 
                    src={image} 
                    alt={`${project.title} gallery - ${project.tags[0]} project visual asset ${index + 1} showing campaign creative and brand materials`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer Navigation */}
        <div className="pt-8 border-t border-border">
          <Link to="/#portfolio">
            <Button variant="outline" className="text-lg px-6 py-3">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Portfolio
            </Button>
          </Link>
        </div>
      </div>
    </div>
    </PageTransition>
  );
};

export default PortfolioDetail;
